import React, { createContext, useEffect, useLayoutEffect, useMemo, useReducer } from 'react';
import { getAllTrndsUserClients, TrndsClient } from '@round/api';
import { useCheckUserGroupsAccess } from '../Modules/Auth/hooks/useCheckUserGroupsAccess';
import useAbortableEffect from '../Hooks/useAbortableEffect';
import useUrlState from '../Hooks/useUrlState';
import { createReducer } from '../helpers';
import { ReducerAction, ReducerActionWithPayload } from '../App.types';
import useNonNullContext from '../Hooks/useNonNullContext';
import useComponentWillUnmount from '../Hooks/useComponentWillUnmount';

type UserClientState = {
    clientsInitialized: boolean;
    hasErrorLoadingClients: boolean;
    clients: TrndsClient[];
    selectedClient: TrndsClient | null;
};

type Actions =
    | ReducerActionWithPayload<'clientsLoaded', TrndsClient[]>
    | ReducerAction<'errorLoadingClients'>
    | ReducerActionWithPayload<'selectClient', TrndsClient>
    | ReducerAction<'clearSelectedClient'>;

const initialState: UserClientState = {
    clientsInitialized: false,
    hasErrorLoadingClients: false,
    clients: [],
    selectedClient: null,
};

const reducer = createReducer<UserClientState, Actions>({
    clientsLoaded: (state, { payload }) => ({
        ...state,
        clients: payload,
        clientsInitialized: true,
        hasErrorLoadingClients: false,
    }),
    errorLoadingClients: (state) => ({ ...state, clientsInitialized: false, hasErrorLoadingClients: true }),
    selectClient: (state, { payload }) => ({ ...state, selectedClient: payload }),
    clearSelectedClient: (state) => ({ ...state, selectedClient: null }),
});

const UserClientsContext = createContext<UserClientState | null>(null);
const UserClientsDispatchContext = createContext<React.Dispatch<Actions> | null>(null);

export const UserClientsContextProvider = ({ children }: { children?: React.ReactNode }) => {
    const shouldFetchClients = useCheckUserGroupsAccess(['trnds_client', 'trnds_admin'], 'any');
    const [state, dispatch] = useReducer(reducer, initialState);

    useAbortableEffect(
        (signal) => {
            if (shouldFetchClients) {
                getAllTrndsUserClients({ signal })
                    .then((clients) => dispatch({ type: 'clientsLoaded', payload: clients }))
                    .catch(() => {});
            }
        },
        [shouldFetchClients]
    );

    return (
        <UserClientsContext.Provider value={state}>
            <UserClientsDispatchContext.Provider value={dispatch}>{children}</UserClientsDispatchContext.Provider>
        </UserClientsContext.Provider>
    );
};

type UseUserClients = UserClientState & {
    selectClient: (client: TrndsClient) => void;
    clear: () => void;
};

export function useUserClients(): UseUserClients {
    const state = useNonNullContext(UserClientsContext);
    const dispatch = useNonNullContext(UserClientsDispatchContext);

    return useMemo(
        () => ({
            ...state,
            selectClient: (client: TrndsClient) => {
                dispatch({ type: 'selectClient', payload: client });
            },
            clear: () => {
                dispatch({ type: 'clearSelectedClient' });
            },
        }),
        [dispatch, state]
    );
}

export const ClientPageWrapper = ({ children }: { children: React.ReactNode | undefined }) => {
    const [urlState, setUrlState] = useUrlState<{ clientId: number }>();
    const { clients, selectedClient, selectClient, clear } = useUserClients();

    useEffect(() => {
        if (!urlState.clientId && !selectedClient && clients.length) {
            selectClient(clients[0]);
            setUrlState({ clientId: clients[0].id });
        }
    }, [clients, selectClient, selectedClient, setUrlState, urlState.clientId]);

    useEffect(() => {
        const urlClient = clients.find((c) => c.id === Number(urlState.clientId));
        if (urlState.clientId && !selectedClient && urlClient) {
            selectClient(urlClient);
        }
    }, [clients, selectClient, selectedClient, urlState.clientId]);

    useLayoutEffect(() => {
        if (selectedClient?.id !== Number(urlState.clientId)) {
            setUrlState({ clientId: selectedClient?.id });
        }
    }, [selectedClient, setUrlState, urlState.clientId]);

    useComponentWillUnmount(() => {
        clear();
    });

    return <>{children}</>;
};
